import { PlanungAdminRoles, PlanungViewerRoles } from '@bp/pim-auth-constants';
import { Grid, GridColumn, GridRow, RadioGroup, Switch } from '@bp/ui-components';
import { useTranslation } from 'react-i18next';
import styles from './UserPermissions.module.scss';

export const UserPermissions = ({
  setFieldValue,
  values,
}: {
  setFieldValue: (
    key: 'isAdmin' | 'deputate' | 'timetable' | 'substitution',
    value: 'no-access' | PlanungAdminRoles | PlanungViewerRoles | boolean,
  ) => Promise<void>;
  values: Record<string, any>;
}) => {
  const { t } = useTranslation();
  return (
    <Grid useFormGap className={styles['user-permissions']}>
      <GridRow spacingBottom='s' spacingTop='s'>
        <GridColumn width={2}>
          <div className={styles['headline-switch']}>{t('profile.isAdmin')}</div>
        </GridColumn>
        <GridColumn width={8}>
          <Switch
            name='isAdmin'
            onChange={async (element) => {
              await setFieldValue('isAdmin', element.target.checked);
            }}
            checked={values.isAdmin}
          />
        </GridColumn>
      </GridRow>

      <GridRow spacingBottom='none' spacingTop='none'>
        <GridColumn width={2}>
          <div className={styles.headline}>{t('deputate.title', { count: 2 })}</div>
        </GridColumn>
        <GridColumn width={8}>
          <RadioGroup
            disabled={values.isAdmin}
            className={styles.radio}
            options={[
              {
                label: t('profile.noAccess'),
                value: 'no-access',
                tooltipText: t('profile.noAccessText', { area: t('deputate.title', { count: 2 }) }),
              },
              {
                label: t('profile.view'),
                value: PlanungViewerRoles.DeputatViewer,
                tooltipText: t('profile.viewText', { area: t('deputate.title', { count: 2 }) }),
              },
              {
                label: t('profile.write'),
                value: PlanungAdminRoles.DeputatAdmin,
                tooltipText: t('profile.writeText', { area: t('deputate.title', { count: 2 }) }),
              },
            ]}
            value={values.deputate ?? 'no-access'}
            onChange={async (value) => {
              await setFieldValue(`deputate`, value as 'no-access' | PlanungAdminRoles | PlanungViewerRoles);
            }}
            name='deputate'
          />
        </GridColumn>
      </GridRow>

      <GridRow spacingBottom='none' spacingTop='none'>
        <GridColumn width={2}>
          <div className={styles.headline}>{t('timetable.title', { count: 2 })}</div>
        </GridColumn>
        <GridColumn width={8}>
          <RadioGroup
            disabled={values.isAdmin}
            className={styles.radio}
            options={[
              {
                label: t('profile.noAccess'),
                value: 'no-access',
                tooltipText: t('profile.noAccessText', { area: t('timetable.title', { count: 2 }) }),
              },
              {
                label: t('profile.view'),
                value: PlanungViewerRoles.StundenViewer,
                tooltipText: t('profile.viewText', { area: t('timetable.title', { count: 2 }) }),
              },
              {
                label: t('profile.write'),
                value: PlanungAdminRoles.StundenAdmin,
                tooltipText: t('profile.writeText', { area: t('timetable.title', { count: 2 }) }),
              },
            ]}
            value={values.timetable ?? 'no-access'}
            onChange={async (value) => {
              await setFieldValue(`timetable`, value as 'no-access' | PlanungAdminRoles | PlanungViewerRoles);
            }}
            name='timetable'
          />
        </GridColumn>
      </GridRow>

      <GridRow spacingBottom='none' spacingTop='none'>
        <GridColumn width={2}>
          <div className={styles.headline}>{t('substitutions.title', { count: 2 })}</div>
        </GridColumn>
        <GridColumn width={8}>
          <RadioGroup
            disabled={values.isAdmin}
            className={styles.radio}
            options={[
              {
                label: t('profile.noAccess'),
                value: 'no-access',
                tooltipText: t('profile.noAccessText', { area: t('substitutions.title', { count: 2 }) }),
              },
              {
                label: t('profile.manage'),
                value: PlanungViewerRoles.VertretungViewer,
                tooltipText: t('profile.manageText', { area: t('substitutions.title', { count: 2 }) }),
              },
              {
                label: t('profile.admin'),
                value: PlanungAdminRoles.VertretungAdmin,
                tooltipText: t('profile.adminText', { area: t('substitutions.title', { count: 2 }) }),
              },
            ]}
            value={values.substitution ?? 'no-access'}
            onChange={async (value) => {
              await setFieldValue(`substitution`, value as 'no-access' | PlanungAdminRoles | PlanungViewerRoles);
            }}
            name='substitution'
          />
        </GridColumn>
      </GridRow>
    </Grid>
  );
};
